import React, { useEffect, useRef, useState, useCallback } from "react";
import "../Style/Uiuxservice.css";
import "../Style/Uiux.css";
import "../Style/Uiuxdesign.css";

import icon1 from "../assets/icon1.png";
import icon2 from "../assets/icon2.png";
import icon3 from "../assets/icon3.png";
import icon4 from "../assets/icon4.png";
import icon5 from "../assets/icon5.png";
import icon6 from "../assets/icon6.png";

const uiuxServices = [
  {
    icon: icon1,
    title: "User Experience (UX) Design",
    desc: "Create intuitive digital experiences that solve real user problems through research-driven design methodologies.",
    tags: [
      "UX Research",
      "User Journey Mapping",
      "Information Architecture",
      "Wireframing",
      "Usability Testing",
    ],
  },
  {
    icon: icon2,
    title: "Modern User Interface (UI) Design",
    desc: "Design visually compelling interfaces that combine aesthetics with functionality.",
    tags: [
      "UI Design",
      "Responsive Design",
      "Mobile-First Design",
      "Visual Design",
      "Accessibility",
    ],
  },
  {
    icon: icon3,
    title: "Landing Page Design & Conversion Optimization",
    desc: "Design landing pages that attract visitors and convert them into customers.",
    tags: [
      "Landing Page Design",
      "Conversion Rate Optimization",
      "CTA Design",
      "Sales Funnels",
      "Marketing Pages",
    ],
  },
  {
    icon: icon4,
    title: "UX Research & Validation",
    desc: "Our research-driven approach ensures every design decision is backed by insights, reducing risk and improving product-market fit.",
    tags: [
      "User Interviews",
      "Competitor Research",
      "UX Audit",
      "Journey Mapping",
      "Product Validation",
    ],
  },
  {
    icon: icon5,
    title: "Wireframing, Prototyping & MVP Design",
    desc: "We build interactive prototypes that allow stakeholders to test ideas quickly, validate concepts, and accelerate product development.",
    tags: [
      "Wireframes",
      "Interactive Prototypes",
      "MVP Design",
      "Clickable Mockups",
      "User Flows",
    ],
  },
  {
    icon: icon6,
    title: "Design Systems & Brand Experience",
    desc: "Maintain consistency across every digital touchpoint.",
    tags: [
      "Design Systems",
      "UI Components",
      "Brand Identity",
      "Style Guides",
      "Design Libraries",
    ],
  },
];

const AUTOPLAY_DELAY = 4500;

function UiuxServicesSection() {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const subtextRef = useRef(null);
  const autoplayRef = useRef(null);

  const [sectionVisible, setSectionVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const total = uiuxServices.length;

  // Scroll-reveal for heading/subtext
  useEffect(() => {
    const targets = [headingRef.current, subtextRef.current].filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setSectionVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Autoplay
  useEffect(() => {
    if (isPaused) return;
    autoplayRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % total);
    }, AUTOPLAY_DELAY);
    return () => clearInterval(autoplayRef.current);
  }, [isPaused, total]);

  const goTo = useCallback((index) => {
    setActiveIndex(((index % total) + total) % total);
  }, [total]);

  const goPrev = () => goTo(activeIndex - 1);
  const goNext = () => goTo(activeIndex + 1);

  // Position of each card relative to the active one (-1, 0, 1, or "far")
  const getPosition = (index) => {
    let diff = index - activeIndex;
    if (diff > total / 2) diff -= total;
    if (diff < -total / 2) diff += total;

    if (diff === 0) return "active";
    if (diff === -1) return "prev";
    if (diff === 1) return "next";
    return "far";
  };

  return (
    <section
      className="uiux-services-section"
      ref={sectionRef}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <span className="uiux-services-watermark" aria-hidden="true">
        SERVICES
      </span>

      <span
        className={`uiux-services-eyebrow ${sectionVisible ? "uiux-reveal" : ""}`}
      >
        What We Offer
      </span>

      <h2
        className={`uiux-services-heading ${sectionVisible ? "uiux-reveal" : ""}`}
        ref={headingRef}
      >
        Experience-Driven UI/UX Design Services for Modern Businesses
      </h2>

      <p
        className={`uiux-services-subtext ${sectionVisible ? "uiux-reveal" : ""}`}
        ref={subtextRef}
      >
        We combine creativity, research, and strategy to design digital products
        that users love. Our comprehensive UI/UX design services focus on
        improving usability, increasing engagement, simplifying customer
        journeys, and delivering outstanding digital experiences that drive
        business success.
      </p>

      <div className="uiux-carousel">
        <button
          type="button"
          className="uiux-carousel-arrow uiux-carousel-arrow-left"
          onClick={goPrev}
          aria-label="Previous service"
        >
          ‹
        </button>

        <div className="uiux-carousel-track">
          {uiuxServices.map((service, index) => {
            const position = getPosition(index);
            return (
              <div
                className={`uiux-service-card is-${position}`}
                key={service.title}
                onClick={() => position !== "active" && goTo(index)}
              >
                <div className="uiux-service-icon-wrap">
                  <img
                    src={service.icon}
                    alt={service.title}
                    className="uiux-service-icon"
                  />
                </div>

                <h3>{service.title}</h3>
                <p>{service.desc}</p>

                <div className="uiux-service-tags">
                  {service.tags.slice(0, 3).map((tag, i) => (
                    <span key={i}>{tag}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          className="uiux-carousel-arrow uiux-carousel-arrow-right"
          onClick={goNext}
          aria-label="Next service"
        >
          ›
        </button>
      </div>

      <div className="uiux-carousel-dots">
        {uiuxServices.map((_, index) => (
          <button
            key={index}
            type="button"
            className={`uiux-dot ${index === activeIndex ? "is-active" : ""}`}
            onClick={() => goTo(index)}
            aria-label={`Go to service ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
}

export default UiuxServicesSection;